import React from 'react';
import { Circle } from '@mui/icons-material';
import { Paper, Stack, Typography } from '@mui/material';
import { DateFormats } from '@uplift/constants';
import { useAllCollaborativeChartsReviewsQuery, useCollaborativeStateDetailsQuery, useSupervisionMeetingsStatsQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { LightChip } from '@uplift/ui-v2';
import { StateLabel } from '../../../../components/StatesLabel';
import { SupervisionMeetingStats } from '@uplift/api';


export type SuperviseeDetailsProps = {
  providerId: string;
};


const MeetingDetails: React.VFC<{ stats: SupervisionMeetingStats }> = ({ stats }) => {
  const { data: collaborativeStateDetails } = useCollaborativeStateDetailsQuery(stats.stateId);

  if (!collaborativeStateDetails?.requiresSupervisionMeeting) {
    return null;
  }

  const dueDate = CalendarDateService.date(stats.nextDueDate, DateFormats.ISO_DATE);
  const formattedDueDate = CalendarDateService.format(dueDate, DateFormats['MMM D, YYYY']);
  const daysOverdue = CalendarDateService.date().diff(dueDate, 'days');
  const isPastDue = daysOverdue > 0;

  return (
    <Stack gap={0.5}>
      <Typography variant="overline" color="secondary" sx={{ fontWeight: 600, lineHeight: 1 }}>
        Next Supervision Meeting Due
      </Typography>
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography component="span" sx={{ fontWeight: 'normal' }}>
          {formattedDueDate}
        </Typography>
        {isPastDue && (
          <LightChip
            size="small"
            color="error"
            icon={<Circle sx={{ fontSize: '8px !important' }} />}
            label={`Past due by ${daysOverdue} days`}
          />
        )}
      </Stack>
    </Stack>
  );
};

const ChartsReviewDetails: React.VFC<{ stats: SupervisionMeetingStats }> = ({ stats }) => {
  const { data: chartsReviews } = useAllCollaborativeChartsReviewsQuery({ submitted: true });
  const lastSubmittedChartsReview = chartsReviews.find(cr => cr.matchId === stats.collaborativeMatchId);

  if (!lastSubmittedChartsReview?.submittedAt) {
    return null;
  }

  return (
    <Stack gap={0.5}>
      <Typography variant="overline" color="secondary" sx={{ fontWeight: 600, lineHeight: 1 }}>
        Last charts review
      </Typography>
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography component="span" fontWeight={450}>
          {CalendarDateService.date(lastSubmittedChartsReview.submittedAt, DateFormats.ISO_DATE).format(DateFormats['MMM D, YYYY'])}
        </Typography>
      </Stack>
    </Stack>
  );
};


const SupervisionMeetingDetails: React.VFC<{ stats: SupervisionMeetingStats }> = ({ stats }) => {
  return (
    <Stack component={Paper} variant="outlined" gap={2} sx={{ p: 2 }}>
      <Typography variant="h6" fontWeight={500}>
        <StateLabel stateId={stats.stateId} />
      </Typography>
      <MeetingDetails stats={stats} />
      <ChartsReviewDetails stats={stats} />
    </Stack>
  );
};


export const SupervisionMeetingsStats: React.VFC<SuperviseeDetailsProps> = ({ providerId }) => {
  const { data: meetingsStats } = useSupervisionMeetingsStatsQuery(providerId);

  return (
    <Stack p={2} gap={1}>
      <Typography variant="h5">Manage Details</Typography>
      {meetingsStats.map(meetingStats => (
        <SupervisionMeetingDetails
          key={meetingStats.stateId}
          stats={meetingStats}
        />
      ))}
    </Stack>
  );
};
